/**
 * Loader persistence: the activation token from an activation link, and the
 * config caches that keep ordinary visitors from calling Dash on every page.
 * All storage access is wrapped: private windows and blocked storage must
 * degrade to "no cache", never throw.
 */

import { isTextSize, type TextSize } from "@/lib/site-feedback/shared/annotations";
import { ACTIVATION_FRAGMENT_KEY, ACTIVATION_RESET_VALUE } from "@/lib/site-feedback/shared/vocabulary";
import type { WidgetConfig } from "../runtime";

const TOKEN_RE = /^[0-9a-f]{32}$/;
const ACTIVATION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const POSITIVE_TTL_MS = 5 * 60 * 1000;
const NEGATIVE_TTL_MS = 60 * 60 * 1000;

const activationKey = (siteKey: string) => `spark-feedback:activation:${siteKey}`;
const configKey = (siteKey: string, token: string | null) => `spark-feedback:config:${siteKey}:${token ?? "none"}`;
const offKey = (siteKey: string) => `spark-feedback:off:${siteKey}`;
// Durable "stop showing" choice that also silences the WordPress admin token
// (data-activation-token), which is re-sent on every admin page view.
const optOutKey = (siteKey: string) => `spark-feedback:optout:${siteKey}`;
const reporterKey = "spark-feedback:reporter";
const textSizeKey = "spark-feedback:text-size";
const sentKey = (siteKey: string) => `spark-feedback:sent:${siteKey}`;

function read(store: Storage | undefined, key: string): unknown {
  try {
    const raw = store?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(store: Storage | undefined, key: string, value: unknown): void {
  try {
    store?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked */
  }
}

function remove(store: Storage | undefined, key: string): void {
  try {
    store?.removeItem(key);
  } catch {
    /* ignore */
  }
}

function local(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function session(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * Pick up `#spark-feedback=<token>` from the current URL (then strip it so it
 * is never bookmarked, shared or sent as a Referer), else the stored token.
 */
export function readActivation(siteKey: string): string | null {
  const hash = location.hash.slice(1);
  const prefix = `${ACTIVATION_FRAGMENT_KEY}=`;
  if (hash.startsWith(prefix)) {
    const token = hash.slice(prefix.length).split("&")[0];
    try {
      history.replaceState(history.state, "", `${location.pathname}${location.search}`);
    } catch {
      /* ignore */
    }
    if (token === ACTIVATION_RESET_VALUE) {
      // "Show Feedback button" (SPARK Site Tools): forget this browser's link and
      // its "stop showing" choice, so a WordPress admin's own access applies again.
      remove(local(), activationKey(siteKey));
      remove(local(), offKey(siteKey));
      remove(local(), optOutKey(siteKey));
      return null;
    }
    if (TOKEN_RE.test(token)) {
      write(local(), activationKey(siteKey), { token, at: Date.now() });
      remove(local(), offKey(siteKey));
      remove(local(), optOutKey(siteKey));
      return token;
    }
  }
  const stored = read(local(), activationKey(siteKey)) as { token?: unknown; at?: unknown } | null;
  if (stored && typeof stored.token === "string" && TOKEN_RE.test(stored.token) && typeof stored.at === "number") {
    if (Date.now() - stored.at < ACTIVATION_TTL_MS) return stored.token;
    remove(local(), activationKey(siteKey));
  }
  return null;
}

/** Forget a stored link token (it no longer works, or the reviewer stopped the button). */
export function clearActivation(siteKey: string): void {
  remove(local(), activationKey(siteKey));
  write(local(), offKey(siteKey), { at: Date.now() });
}

/** "Stop showing the feedback button on this browser": also silences the WordPress admin token. */
export function optOut(siteKey: string): void {
  clearActivation(siteKey);
  write(local(), optOutKey(siteKey), { at: Date.now() });
}

export function isOptedOut(siteKey: string): boolean {
  return read(local(), optOutKey(siteKey)) !== null;
}

export type CachedConfig = { kind: "on"; config: WidgetConfig } | { kind: "off" } | null;

export function readCachedConfig(siteKey: string, token: string | null): CachedConfig {
  const positive = read(session(), configKey(siteKey, token)) as { at?: number; config?: WidgetConfig } | null;
  if (positive?.config && typeof positive.at === "number" && Date.now() - positive.at < POSITIVE_TTL_MS) {
    return { kind: "on", config: positive.config };
  }
  if (!token) {
    const negative = read(local(), offKey(siteKey)) as { at?: number } | null;
    if (negative && typeof negative.at === "number" && Date.now() - negative.at < NEGATIVE_TTL_MS) return { kind: "off" };
  }
  return null;
}

export function writeCachedConfig(siteKey: string, token: string | null, config: WidgetConfig | null): void {
  if (config) {
    write(session(), configKey(siteKey, token), { at: Date.now(), config });
    remove(local(), offKey(siteKey));
  } else {
    remove(session(), configKey(siteKey, token));
    if (!token) write(local(), offKey(siteKey), { at: Date.now() });
  }
}

export interface RememberedReporter {
  name: string;
  email: string;
}

export function readReporter(): RememberedReporter | null {
  const value = read(local(), reporterKey) as Partial<RememberedReporter> | null;
  return value && typeof value.name === "string" && typeof value.email === "string" ? { name: value.name, email: value.email } : null;
}

export function writeReporter(reporter: RememberedReporter): void {
  write(local(), reporterKey, reporter);
}

/** The reviewer's last text size for screenshot notes. */
export function readTextSize(): TextSize | null {
  const value = read(local(), textSizeKey);
  return isTextSize(value) ? value : null;
}

export function writeTextSize(size: TextSize): void {
  write(local(), textSizeKey, size);
}

/** Keep the follow links of tickets sent from this browser (newest first, max 50). */
export function rememberSent(siteKey: string, entry: { number: number; reference?: string; followUrl: string; at: number }): void {
  const list = (read(local(), sentKey(siteKey)) as Array<typeof entry> | null) ?? [];
  write(local(), sentKey(siteKey), [entry, ...list.filter((e) => e.followUrl !== entry.followUrl)].slice(0, 50));
}
